import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import MuiSelect from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/system'
import type { HTMLAttributes, ReactNode } from 'react'
import { useMemo } from 'react'

import { LuneThemeNew } from '../theme'

import FieldValidationError from './FieldValidationError'
import { getInputBaseStyles } from './InputNew'

const { palette, spacing } = LuneThemeNew

export interface Props<T> {
    value: T
    items: {
        value: T
        label: string
        icon?: ReactNode
        content?: ReactNode
        dividerAfter?: boolean
    }[]
    onChange: (value: T) => void
    onClose?: () => void
    sx?: SxProps
    menuSx?: SxProps
    menuElevation?: number
    disabled?: boolean
    inferred?: boolean
    readOnly?: boolean
    error?: string | boolean
    hint?: string
    placeholder?: string
    wide?: boolean
    startAdornment?: ReactNode
    endAdornment?: ReactNode
    size?: 'sm' | 'md'
    className?: string
    /** Applied to the role="combobox" element. */
    'data-testid'?: string
}

/**
 * Controlled select component styled to match InputNew
 * Supports using objects as values (not just strings/numbers),
 * which is useful for complex forms
 * @param value - the currently selected value - its inferred type is used to infer the type of the items' value
 * @param items - array of objects with value and label properties
 * @param onChange - callback to be called with the selected item's value
 * @param onClose - callback to be called when the item list is closed
 * @param disabled - disables select
 * @param inferred - applies inferred input styling
 * @param readOnly - makes select read-only
 * @param error - used to show error state (string for error message, boolean for error styling)
 * @param hint - helper text shown below the input
 * @param placeholder - placeholder text when no value is selected
 * @param wide - makes select full width
 * @param startAdornment - icon or element to display at the start
 * @param endAdornment - icon or element to display at the end (defaults to dropdown arrow)
 * @param size - size of the input ('sm' or 'md')
 * @param menuSx - custom styles for the dropdown menu
 * @param menuElevation - elevation for the dropdown menu
 * @param sx - custom styles for the select input
 */
// eslint-disable-next-line complexity
const SelectNew = <T,>({
    value,
    items,
    onChange,
    onClose,
    sx,
    menuSx,
    menuElevation,
    disabled,
    inferred,
    readOnly,
    error,
    hint,
    placeholder,
    wide,
    startAdornment,
    endAdornment,
    size = 'md',
    className,
    'data-testid': dataTestId,
    ...rest
}: Props<T>) => {
    const StyledSelect = useMemo(
        () =>
            styled(MuiSelect<T>)(
                LuneThemeNew.unstable_sx({
                    ...(getInputBaseStyles(size, wide, '.MuiSelect-select') as Record<
                        string,
                        unknown
                    >),
                    fieldset: {
                        border: 'none',
                    },
                    '.MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0,
                        paddingRight: spacing(3),
                    },
                }),
            ),
        [wide, size],
    )

    const selectedItem = items.find((item) => item.value === value)
    const displayValue = selectedItem ? selectedItem.label : ''

    return (
        <>
            <StyledSelect
                data-testid="combobox"
                // Puts the caller's testid on the role="combobox" element.
                SelectDisplayProps={
                    dataTestId
                        ? ({ 'data-testid': dataTestId } as HTMLAttributes<HTMLDivElement>) // data-* isn't in HTMLAttributes' type but React forwards it regardless
                        : undefined
                }
                MenuProps={{
                    PaperProps: {
                        elevation: menuElevation ?? 4,
                        sx: {
                            borderRadius: spacing(1),
                            '.MuiList-root': {
                                padding: `${spacing(1)} 0`,
                            },
                            '& .MuiMenuItem-root': {
                                padding: `0 ${spacing(2)}`,
                                minHeight: '48px',
                                '&.Mui-selected': {
                                    backgroundColor: palette.Grey100,
                                },
                            },
                            ...menuSx,
                        },
                    },
                }}
                className={`${className || ''} ${
                    disabled
                        ? 'disabled'
                        : readOnly
                          ? 'readOnly'
                          : error
                            ? 'error'
                            : inferred
                              ? 'inferred'
                              : ''
                }`}
                value={value}
                displayEmpty
                onChange={(event) => onChange(event.target.value as T)}
                disableUnderline
                disabled={disabled || readOnly}
                renderValue={(selected) => {
                    if (!selected && placeholder) {
                        return (
                            <Typography
                                sx={{
                                    color: palette.TextPlaceholder,
                                    fontSize: size === 'md' ? '16px' : '14px',
                                }}
                            >
                                {placeholder}
                            </Typography>
                        )
                    }
                    if (selectedItem?.icon) {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing(1.5) }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& svg': {
                                            height: spacing(2.5),
                                            width: spacing(2.5),
                                        },
                                    }}
                                >
                                    {selectedItem.icon}
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: size === 'md' ? '16px' : '14px',
                                        lineHeight: size === 'md' ? '24px' : '20px',
                                    }}
                                >
                                    {displayValue}
                                </Typography>
                            </Box>
                        )
                    }
                    return (
                        <Typography
                            sx={{
                                fontSize: size === 'md' ? '16px' : '14px',
                                lineHeight: size === 'md' ? '24px' : '20px',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )
                }}
                startAdornment={
                    startAdornment ? (
                        <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ) : undefined
                }
                endAdornment={
                    endAdornment ? (
                        <InputAdornment position="end">{endAdornment}</InputAdornment>
                    ) : undefined
                }
                sx={sx}
                {...(onClose && { onClose })}
                {...rest}
            >
                {items.map((item, index) => [
                    <MenuItem key={`item-${index}`} value={item.value as any}>
                        {item.content ??
                            (item.icon ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Box
                                        sx={{
                                            width: spacing(3),
                                            height: spacing(3),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: spacing(3),
                                            '& svg': {
                                                width: spacing(3),
                                                height: spacing(3),
                                            },
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography sx={{ color: palette.Grey900 }} variant={`body2`}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography sx={{ color: palette.Grey900 }} variant={`body2`}>
                                    {item.label}
                                </Typography>
                            ))}
                    </MenuItem>,
                    item.dividerAfter && <Divider key={`divider-${index}`} sx={{ my: 0.5 }} />,
                ])}
            </StyledSelect>
            {(!error || typeof error !== `string`) && hint && (
                <Typography
                    variant={'caption'}
                    sx={{
                        display: 'block',
                        color: palette.Grey600,
                        mt: 1,
                    }}
                >
                    {hint}
                </Typography>
            )}
            {typeof error === `string` && <FieldValidationError error={error} />}
        </>
    )
}

export default SelectNew
